import React, { useEffect, useState, useRef } from 'react'; // ★ useRef 꼭 확인하세요!
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import axios from 'axios';
import styles from './MultiLargeFileUploads.module.css';
import uploadIcon from '../../img/icon/upload_icon_tranparent2.png';
import Swal from 'sweetalert2';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const ANALYSIS_SERVER_URL = "http://192.168.177.202:4000";

const MultiLargeFileUploads = ({ onClose, type }) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadType, setUploadType] = useState(type);
    const [duplicateFiles, setDuplicateFiles] = useState([]);
    const [orderNumber, setOrderNumber] = useState("");

    // ★ 숨겨진 파일 input을 조작하기 위한 ref 추가
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        setFiles(Array.from(event.target.files));
        setDuplicateFiles([]);
    };

    const clickRNA = () => setUploadType('RNA');
    const clickWES = () => setUploadType('WES');

    // 개별 파일 청크 업로드 함수
    const uploadFileInChunks = async (file, uploadToken, currentFileIndex, totalFilesCount) => {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        // 서버 측 파일명 규칙 적용 (기존 백엔드 로직 이식)
        let targetFileName = file.name;
        if (file.name.endsWith('.txt')) {
            targetFileName = 'md5sums.txt';
        } else if (file.name.endsWith('.fastq.gz') && !file.name.startsWith('G')) {
            targetFileName = `G${file.name}`;
        }

        const statusResponse = await axios.get(`${ANALYSIS_SERVER_URL}/upload/status`, {
            params: { fileName: targetFileName, orderNumber },
            headers: { Authorization: `Bearer ${uploadToken}` }
        });

        const uploadedChunks = statusResponse.data.uploadedChunks || [];

        for (let i = 0; i < totalChunks; i++) {
            if (uploadedChunks.includes(i)) continue;

            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const formData = new FormData();
            formData.append('chunk', chunk);
            formData.append('index', i);
            formData.append('totalChunks', totalChunks);
            formData.append('fileName', targetFileName);
            formData.append('orderNumber', orderNumber);

            await axios.post(`${ANALYSIS_SERVER_URL}/upload/chunk`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${uploadToken}`
                }
            });

            const overallProgress = Math.round(
                ((currentFileIndex / totalFilesCount) * 100) +
                ((i + 1) / totalChunks * (100 / totalFilesCount))
            );
            setUploadProgress(overallProgress);
        }
        return targetFileName;
    };

    const handleFileUpload = async () => {
        // 1️⃣ 유효성 검사 (수주번호 필수 체크 제거)
        // if (files.length === 0) { alert(t('Please select files.')); return; }
        // 수정 후 코드
        if (files.length === 0) {
            Swal.fire({
                icon: 'warning', // 'success', 'error', 'info' 등으로 변경 가능
                title: t('알림'),
                text: t('Please select files.'),
                confirmButtonColor: '#007bff'
            });
            return;
        }
        if (files.length < 3 || files.length % 2 === 0) { alert(t('At least 3 files and odd number required.')); return; }

        // orderNumber가 비어있으면 null 혹은 빈 문자열로 처리 (기존 백엔드 호환을 위해 "" 권장)
        const finalOrderNumber = orderNumber || "";

        const firstFastq = files.find(f => f.name.endsWith('.fastq.gz'));
        if (!firstFastq) { alert(t('No .fastq.gz files found.')); return; }

        const prefix = firstFastq.name.split('_')[0];
        const inferredType = prefix.endsWith('R') ? 'RNA' : prefix.endsWith('E') ? 'WES' : uploadType;

        if (uploadType !== inferredType) {
            alert(t('설정된 업로드 타입과 파일의 업로드 타입이 다릅니다.'));
            return;
        }

        setUploading(true);

        try {
            // 2️⃣ 중복 검사 (EC2 백엔드)
            const fileNamesForCheck = Array.from(new Set(
                files
                    .filter(f => !f.name.endsWith('.txt')) // .txt 파일(md5sums)은 중복 체크에서 제외
                    .map(f => f.name.split('_')[0])
            ));

            const checkRes = await axiosInstance.post(`/checkDuplicates/?type=${inferredType}`, {
                fileNames: fileNamesForCheck
            });

            if (checkRes.data.duplicates?.length > 0) {
                setDuplicateFiles(checkRes.data.duplicates);
                alert(t('Duplicate Files Found'));
                setUploading(false);
                return;
            }

            // 3️⃣ 사전 등록 및 티켓 발급 (finalOrderNumber 사용)
            const preRegisterRes = await axiosInstance.post('/preRegisterUpload', {
                orderNumber: finalOrderNumber,
                uploadType: inferredType,
                sampleNames: fileNamesForCheck
            });
            const { uploadToken } = preRegisterRes.data;

            // 4️⃣ 분석 서버로 직접 청크 업로드 (finalOrderNumber 전달)
            const uploadedActualNames = [];
            for (let i = 0; i < files.length; i++) {
                const finalName = await uploadFileInChunks(files[i], uploadToken, i, files.length, finalOrderNumber);
                uploadedActualNames.push(finalName);
            }

            // 5️⃣ 분석 서버에 병합 및 분석 시작 요청
            await axios.post(`${ANALYSIS_SERVER_URL}/upload/merge`, {
                orderNumber: finalOrderNumber,
                uploadType: inferredType,
                fileNames: uploadedActualNames
            }, {
                headers: { Authorization: `Bearer ${uploadToken}` }
            });

            setUploadProgress(100);
            alert(t('Files uploaded successfully. Analysis started.'));
            onClose();

        } catch (error) {
            console.error('Upload Error:', error);
            
            // 1. 에러 메시지 텍스트 뽑아내기
            const errorMessage = error.response?.data?.message || error.message;

            // ★ 2. EC2 서버로 에러 로그 전송 (추가된 부분)
            axios.post('/api/client-error-log', {
                message: `Target: 192.168.177.202, Order: ${finalOrderNumber}, Error: ${errorMessage}`
            }).catch(e => console.log('에러 로그 전송 실패:', e)); // 혹시 로그 전송조차 실패해도 앱이 뻗지 않게 처리

            // 3. 사용자에게 알림 띄우기
            alert(`${t('오류 발생:')} ${errorMessage}`);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={!uploading ? onClose : (e) => e.stopPropagation()}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <span>{t('Multi Large File Uploads (Direct)')}</span>
                    <button className={styles.closeButton} onClick={!uploading ? onClose : undefined}>X</button>
                </div>
                <div className={styles.buttonGroup}>
                    <div onClick={clickRNA} className={`${styles.button} ${uploadType === 'RNA' ? styles.active : ''}`}>RNA</div>
                    <div onClick={clickWES} className={`${styles.button} ${uploadType === 'WES' ? styles.active : ''}`}>WES</div>
                </div>
                <div className={styles.orderNumber}>
                    <span className={styles.orderLabel}>{t('수주번호')}</span>
                    <input type="text" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} disabled={uploading} />
                </div>

                {/* ★ 커스텀 파일 업로드 UI 시작 */}
                <div className={styles.textInput} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        disabled={uploading}
                        ref={fileInputRef} // ref 연결
                        style={{ display: 'none' }} // 진짜 input은 숨김!
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current.click()} // 클릭 시 숨겨진 input 대신 클릭
                        disabled={uploading}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            color: '#374151'
                        }}
                    >
                        {t('파일 선택')}
                    </button>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                        {files.length > 0 ? `${files.length}${t('개 파일 선택됨')}` : t('선택된 파일 없음')}
                    </span>
                </div>
                {/* ★ 커스텀 파일 업로드 UI 끝 */}

                {duplicateFiles.length > 0 && (
                    <div className={styles.errorList}>
                        <h4>{t('Duplicate Files Found')}:</h4>
                        <ul>{duplicateFiles.map((f, i) => <li key={i}>{f}</li>)}</ul>
                    </div>
                )}
                {files.length > 0 && (
                    <div className={styles.uploadDetails}>
                        {files.map((f, i) => <p key={i}>{f.name} - {(f.size / (1024 * 1024)).toFixed(2)} MB</p>)}
                    </div>
                )}
                <div className={styles.draftButtonBox}>
                    <button className={styles.submitButton} onClick={handleFileUpload} disabled={uploading}>
                        <img src={uploadIcon} className={styles.uploadIcon} alt='upload' />
                        {uploading ? `${t('Uploading')}... (${uploadProgress}%)` : t('Upload')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MultiLargeFileUploads;